import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import ErrorBoundary from '../components/ErrorBoundary';
import UnblockWebImageUploadSection from '../components/UnblockWebImageUploadSection';
import UnblockWebConfigClicksSparkline from '../components/UnblockWebConfigClicksSparkline';
import { getUnblockWebImageUrl } from '../utils/unblockWebAssets';
import { formatSingaporeDateDmyFull, toSingaporeDateInputValue } from '../utils/singaporeTime';

function formatHideBlankLabel(hidden, showBlank) {
    if (hidden && showBlank) return 'Hide/Blank';
    if (hidden) return 'Hide';
    if (showBlank) return 'Blank';
    return '-';
}

const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-3';
const highlightInputClass = `${inputClass} bg-yellow-100`;

const emptyForm = {
    name: '',
    image: '',
    shortPath: '',
    url: '',
    hidden: false,
    showBlank: false,
    renewalDate: '',
    lastPostedDate: '',
    listIndex: 0,
    remarks: '',
};

function ModalShell({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-[200] bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
            <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white mb-10">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <FiX />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function ConfigFormFields({ formData, onChange, onImageChange, onGenerateShortPath, generatingShortPath, disabled }) {
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">List index</label>
                    <p className="text-xs text-gray-500 mt-0.5">Arrange List Order Number</p>
                    <input type="number" name="listIndex" value={formData.listIndex} onChange={onChange} className={inputClass} required />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="text-xs text-gray-500 mt-0.5">For chart usage</p>
                    <input type="text" name="name" value={formData.name} onChange={onChange} className={inputClass} />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Renewal date</label>
                    <p className="text-xs text-gray-500 mt-0.5">For info only</p>
                    <input type="date" name="renewalDate" value={formData.renewalDate} onChange={onChange} className={inputClass} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Last posted date (SG)</label>
                    <p className="text-xs text-gray-500 mt-0.5">Show Date on Website, Change when update link</p>
                    <input type="date" name="lastPostedDate" value={formData.lastPostedDate} onChange={onChange} className={highlightInputClass} />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                            type="checkbox"
                            name="hidden"
                            checked={formData.hidden}
                            onChange={onChange}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Hidden
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5 ml-6">Hide row on Website</p>
                </div>
                <div>
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                            type="checkbox"
                            name="showBlank"
                            checked={formData.showBlank}
                            onChange={onChange}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Show blank
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5 ml-6">Show on Website unclickable button</p>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Short path</label>
                <p className="text-xs text-gray-500 mt-0.5">Must be unique across Web Config and TG Config</p>
                <div className="mt-1 flex gap-2">
                    <input
                        type="text"
                        name="shortPath"
                        value={formData.shortPath}
                        onChange={onChange}
                        className="block flex-1 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-3"
                        placeholder="Unique path code"
                        autoComplete="off"
                    />
                    <button
                        type="button"
                        onClick={onGenerateShortPath}
                        disabled={disabled || generatingShortPath}
                        className="shrink-0 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 whitespace-nowrap"
                    >
                        {generatingShortPath ? 'Generating...' : 'Generate'}
                    </button>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">URL</label>
                <input type="url" name="url" value={formData.url} onChange={onChange} className={highlightInputClass} />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Remarks</label>
                <p className="text-xs text-gray-500 mt-0.5">Remarks will show on website</p>
                <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={onChange}
                    rows={3}
                    className={inputClass}
                    placeholder="Optional notes"
                />
            </div>

            <UnblockWebImageUploadSection
                image={formData.image}
                onImageChange={onImageChange}
                disabled={disabled}
            />
        </div>
    );
}

const UnblockWebConfigPageContent = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [hiddenFilter, setHiddenFilter] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generatingShortPath, setGeneratingShortPath] = useState(false);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const fetchItems = useCallback(async (page = 1, search = '', hidden = '') => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/unblock-web-config?page=${page}&limit=20&sort=listIndex`;
            if (search) apiUrl += `&search=${encodeURIComponent(search)}`;
            if (hidden) apiUrl += `&hidden=${encodeURIComponent(hidden)}`;

            const response = await axios.get(apiUrl, { headers: authHeaders() });
            if (response.data.success) {
                const rows = response.data.data;
                setItems(Array.isArray(rows) ? rows : []);
                setTotalItems(response.data.pagination?.total || 0);
                setTotalPages(Math.max(1, response.data.pagination?.pages || 1));
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to fetch configs');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchItems(currentPage, searchTerm, hiddenFilter);
    }, [currentPage, searchTerm, hiddenFilter, fetchItems]);

    const resetForm = () => {
        setFormData(emptyForm);
    };

    const fetchCreateDefaults = async () => {
        setGeneratingShortPath(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/unblock-web-config/generate-short-path`,
                { headers: authHeaders() }
            );
            if (response.data.success) {
                const nextListIndex = Number(response.data.data?.nextListIndex);
                setFormData((prev) => ({
                    ...prev,
                    shortPath: response.data.data?.shortPath || prev.shortPath,
                    listIndex: Number.isFinite(nextListIndex) ? nextListIndex : prev.listIndex,
                }));
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to generate short path');
        } finally {
            setGeneratingShortPath(false);
        }
    };

    const handleGenerateShortPath = async () => {
        setGeneratingShortPath(true);
        setError('');
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/unblock-web-config/generate-short-path`,
                { headers: authHeaders() }
            );
            if (response.data.success) {
                setFormData((prev) => ({
                    ...prev,
                    shortPath: response.data.data?.shortPath || '',
                }));
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to generate short path');
        } finally {
            setGeneratingShortPath(false);
        }
    };

    const openCreate = () => {
        resetForm();
        setShowCreateModal(true);
        fetchCreateDefaults();
    };

    const openEdit = (item) => {
        setSelectedItem(item);
        setFormData({
            name: item.name || '',
            image: item.image || '',
            shortPath: item.shortPath || '',
            url: item.url || '',
            hidden: Boolean(item.hidden),
            showBlank: Boolean(item.showBlank),
            renewalDate: toSingaporeDateInputValue(item.renewalDate),
            lastPostedDate: toSingaporeDateInputValue(item.lastPostedDate),
            listIndex: item.listIndex ?? 0,
            remarks: item.remarks || '',
        });
        setShowEditModal(true);
    };

    const openView = (item) => {
        setSelectedItem(item);
        setShowViewModal(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleImageChange = (filename) => {
        setFormData((prev) => ({ ...prev, image: filename || '' }));
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/unblock-web-config`,
                {
                    ...formData,
                    shortPath: formData.shortPath.trim() || null,
                    listIndex: Number(formData.listIndex) || 0,
                    renewalDate: formData.renewalDate || null,
                    lastPostedDate: formData.lastPostedDate || null,
                },
                { headers: authHeaders() }
            );
            setShowCreateModal(false);
            resetForm();
            fetchItems(currentPage, searchTerm, hiddenFilter);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to create config');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedItem) return;
        setSaving(true);
        setError('');
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/unblock-web-config/${selectedItem._id}`,
                {
                    ...formData,
                    shortPath: formData.shortPath.trim() || null,
                    listIndex: Number(formData.listIndex) || 0,
                    renewalDate: formData.renewalDate || null,
                    lastPostedDate: formData.lastPostedDate || null,
                },
                { headers: authHeaders() }
            );
            setShowEditModal(false);
            setSelectedItem(null);
            resetForm();
            fetchItems(currentPage, searchTerm, hiddenFilter);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to update config');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedItem) return;
        setSaving(true);
        setError('');
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/unblock-web-config/${selectedItem._id}`,
                { headers: authHeaders() }
            );
            setShowDeleteModal(false);
            setSelectedItem(null);
            fetchItems(currentPage, searchTerm, hiddenFilter);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to delete config');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Unblock Web Config</h1>
                        <p className="text-gray-600">Total: {totalItems} entries</p>
                    </div>
                    <button
                        onClick={openCreate}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Add entry
                    </button>
                </div>

                <div className="mb-6 flex flex-col lg:flex-row gap-2">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                            placeholder="Search by name, URL, short path, remarks..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (setSearchTerm(searchInput), setCurrentPage(1))}
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <FiSearch className="h-5 w-5 text-gray-400" />
                        </div>
                    </div>
                    <select
                        value={hiddenFilter}
                        onChange={(e) => {
                            setHiddenFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-blue-700 font-bold min-w-[160px]"
                    >
                        <option value="">All visibility</option>
                        <option value="false">Visible only</option>
                        <option value="true">Hidden only</option>
                    </select>
                    <button
                        onClick={() => {
                            setSearchTerm(searchInput);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiSearch className="mr-2" />
                        Search
                    </button>
                    <button
                        onClick={() => {
                            setSearchInput('');
                            setSearchTerm('');
                            setHiddenFilter('');
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                        Clear
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}

                {loading ? (
                    <p className="text-gray-600">Loading...</p>
                ) : (
                    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Index</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hide/Blank</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Posted</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {items.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                                            No entries found.
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((item) => (
                                        <tr key={item._id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-bold text-blue-700">{item.listIndex ?? 0}</td>
                                            <td className="px-4 py-3">
                                                {item.image ? (
                                                    <img
                                                        src={getUnblockWebImageUrl(item.image)}
                                                        alt={item.name || 'Config'}
                                                        className="h-10 w-10 object-cover rounded border"
                                                    />
                                                ) : (
                                                    <span className="text-gray-400 text-sm">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-blue-600 max-w-md truncate">
                                                {item.url ? (
                                                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                                        {item.url}
                                                    </a>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{formatHideBlankLabel(item.hidden, item.showBlank)}</td>
                                            <td className="px-4 py-3 text-sm">{formatSingaporeDateDmyFull(item.renewalDate)}</td>
                                            <td className="px-4 py-3 text-sm">{formatSingaporeDateDmyFull(item.lastPostedDate)}</td>
                                            <td className="px-4 py-3">
                                                <UnblockWebConfigClicksSparkline clickrateObject={item.clickrateObject} />
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="flex gap-2">
                                                    <button onClick={() => openView(item)} className="text-blue-600 hover:text-blue-800" title="View">
                                                        <FiEye />
                                                    </button>
                                                    <button onClick={() => openEdit(item)} className="text-green-600 hover:text-green-800" title="Edit">
                                                        <FiEdit2 />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedItem(item);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        className="text-red-600 hover:text-red-800"
                                                        title="Delete"
                                                    >
                                                        <FiTrash2 />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <Pagination
                    currentPage={Math.max(1, currentPage)}
                    totalPages={Math.max(1, totalPages)}
                    onPageChange={(page) => setCurrentPage(Math.max(1, page))}
                />

                {showCreateModal && (
                    <ModalShell title="Add Unblock Web Config" onClose={() => !saving && setShowCreateModal(false)}>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <ConfigFormFields
                                formData={formData}
                                onChange={handleChange}
                                onImageChange={handleImageChange}
                                onGenerateShortPath={handleGenerateShortPath}
                                generatingShortPath={generatingShortPath}
                                disabled={saving}
                            />
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowCreateModal(false)} disabled={saving} className="px-4 py-2 bg-gray-100 rounded-md">
                                    Cancel
                                </button>
                                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
                                    {saving ? 'Saving...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </ModalShell>
                )}

                {showEditModal && (
                    <ModalShell title="Edit Unblock Web Config" onClose={() => !saving && setShowEditModal(false)}>
                        <form onSubmit={handleUpdate} className="space-y-4">
                            <ConfigFormFields
                                formData={formData}
                                onChange={handleChange}
                                onImageChange={handleImageChange}
                                onGenerateShortPath={handleGenerateShortPath}
                                generatingShortPath={generatingShortPath}
                                disabled={saving}
                            />
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowEditModal(false)} disabled={saving} className="px-4 py-2 bg-gray-100 rounded-md">
                                    Cancel
                                </button>
                                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </ModalShell>
                )}

                {showViewModal && selectedItem && (
                    <ModalShell title="View Unblock Web Config" onClose={() => setShowViewModal(false)}>
                        <div className="space-y-3 text-sm text-gray-700">
                            {selectedItem.image && (
                                <img
                                    src={getUnblockWebImageUrl(selectedItem.image)}
                                    alt={selectedItem.name || 'Config'}
                                    className="h-24 w-24 object-cover rounded border"
                                />
                            )}
                            <p><span className="font-semibold">Name:</span> {selectedItem.name || '—'}</p>
                            <p><span className="font-semibold">Short path:</span> {selectedItem.shortPath || '—'}</p>
                            <p><span className="font-semibold">URL:</span> {selectedItem.url || '—'}</p>
                            <p><span className="font-semibold">Remarks:</span> {selectedItem.remarks || '—'}</p>
                            <p><span className="font-semibold">List index:</span> {selectedItem.listIndex ?? 0}</p>
                            <p><span className="font-semibold">Hidden:</span> {selectedItem.hidden ? 'Yes' : 'No'}</p>
                            <p><span className="font-semibold">Show blank:</span> {selectedItem.showBlank ? 'Yes' : 'No'}</p>
                            <p><span className="font-semibold">Renewal date:</span> {formatSingaporeDateDmyFull(selectedItem.renewalDate)}</p>
                            <p><span className="font-semibold">Last posted date:</span> {formatSingaporeDateDmyFull(selectedItem.lastPostedDate)}</p>
                            <p><span className="font-semibold">Created:</span> {formatSingaporeDateDmyFull(selectedItem.createdAt)}</p>
                        </div>
                    </ModalShell>
                )}

                {showDeleteModal && selectedItem && (
                    <ModalShell title="Delete entry" onClose={() => !saving && setShowDeleteModal(false)}>
                        <p className="text-sm text-gray-700 mb-4">
                            Delete <span className="font-semibold">{selectedItem.name || 'this entry'}</span>? This also removes its R2 image.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button type="button" onClick={() => setShowDeleteModal(false)} disabled={saving} className="px-4 py-2 bg-gray-100 rounded-md">
                                Cancel
                            </button>
                            <button type="button" onClick={handleDelete} disabled={saving} className="px-4 py-2 bg-red-600 text-white rounded-md disabled:opacity-50">
                                {saving ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </ModalShell>
                )}
            </div>
    );
};

const UnblockWebConfigPage = () => (
    <Layout>
        <ErrorBoundary
            fallbackTitle="Unblock Web Config Error"
            fallbackDescription="This page failed to load. Restart the client dev server after pulling changes, then try again."
        >
            <UnblockWebConfigPageContent />
        </ErrorBoundary>
    </Layout>
);

export default UnblockWebConfigPage;
